// AccommodationContext.js - Version corrigée PHASE 2
// ✅ Vérification du module actif avant tout appel API
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import accommodationService from '../views/accommodations/services/accommodationService'
import { useNotification } from './NotificationContext'
import { useLocation } from 'react-router-dom'
import { useModules } from './ModuleContext'

const AccommodationContext = createContext()

export const useAccommodation = () => {
  const context = useContext(AccommodationContext)
  if (!context) {
    throw new Error('useAccommodation must be used within an AccommodationProvider')
  }
  return context
}

export const AccommodationProvider = ({ children }) => {
  const [accommodations, setAccommodations] = useState([])
  const [totalPages, setTotalPages] = useState(1)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [filters, setFilters] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const { addToast } = useNotification()
  const location = useLocation()

  // ✅ MULTI-TENANT: Vérification du module accommodations
  const { isModuleEnabled, loading: modulesLoading } = useModules()
  const isAccommodationsEnabled = isModuleEnabled('accommodations')

  // Security: userId in localStorage is for UI state only - auth handled via httpOnly cookies
  const isAuthenticated = !!localStorage.getItem('userId')

  // Vérifier si l'utilisateur est sur une page publique
  const isPublicPage = ['/login', '/register', '/404', '/500'].some((path) =>
    location.pathname.includes(path),
  )

  const fetchAccommodations = useCallback(
    async (page = 1, limit = 6, currentFilters = filters, currentSearchTerm = searchTerm) => {
      // ✅ MULTI-TENANT: Ne pas effectuer l'appel si le module est désactivé
      if (!isAccommodationsEnabled) {
        console.log('🔇 AccommodationContext: Module accommodations désactivé, skip fetch')
        setAccommodations([])
        setTotalPages(1)
        return
      }

      // Ne pas effectuer l'appel si l'utilisateur n'est pas authentifié ou est sur une page publique
      if (!isAuthenticated || isPublicPage) {
        return
      }

      try {
        setLoading(true)
        setError(null)

        console.log('Fetching accommodations:', { page, limit, currentFilters, currentSearchTerm })

        const data = await accommodationService.getAccommodations(
          page,
          limit,
          currentFilters,
          currentSearchTerm,
        )

        console.log('Accommodations fetched:', data)

        setAccommodations(data.accommodations || [])
        setTotalPages(data.totalPages || 1)
        setCurrentPage(page)
      } catch (err) {
        console.error('Erreur lors de la récupération des hébergements:', err)
        setError(err.message || 'Erreur lors de la récupération des hébergements')
        addToast(err.message || 'Erreur lors de la récupération des hébergements', 'danger')

        // En cas d'erreur, réinitialiser les données
        setAccommodations([])
        setTotalPages(1)
      } finally {
        setLoading(false)
      }
    },
    [filters, searchTerm, addToast, isAuthenticated, isPublicPage, isAccommodationsEnabled],
  )

  const createAccommodation = async (accommodationData, photos = []) => {
    // ✅ MULTI-TENANT: Vérifier module actif
    if (!isAccommodationsEnabled) {
      console.warn('🔇 AccommodationContext: Module accommodations désactivé')
      addToast('Le module hébergements est désactivé', 'warning')
      return null
    }

    try {
      setLoading(true)
      setError(null)

      console.log('Creating accommodation:', { accommodationData, photosCount: photos.length })

      const response = await accommodationService.createAccommodation(accommodationData, photos)

      console.log('Accommodation created:', response)

      // Rafraîchir la liste des hébergements après création
      await fetchAccommodations(1)

      addToast('Hébergement créé avec succès', 'success')
      return response
    } catch (err) {
      console.error("Erreur lors de la création de l'hébergement:", err)
      setError(err.message || "Erreur lors de la création de l'hébergement")
      addToast(err.message || "Erreur lors de la création de l'hébergement", 'danger')
      throw err
    } finally {
      setLoading(false)
    }
  }

  const updateAccommodation = async (id, accommodationData, options = {}) => {
    // ✅ MULTI-TENANT: Vérifier module actif
    if (!isAccommodationsEnabled) {
      console.warn('🔇 AccommodationContext: Module accommodations désactivé')
      addToast('Le module hébergements est désactivé', 'warning')
      return null
    }

    try {
      setLoading(true)
      setError(null)

      console.log('Updating accommodation:', { id, accommodationData, options })

      const response = await accommodationService.updateAccommodation(
        id,
        accommodationData,
        options,
      )

      console.log('Accommodation updated:', response)

      // Rafraîchir la liste des hébergements après mise à jour
      await fetchAccommodations(currentPage)

      addToast('Hébergement mis à jour avec succès', 'success')
      return response
    } catch (err) {
      console.error("Erreur lors de la mise à jour de l'hébergement:", err)
      setError(err.message || "Erreur lors de la mise à jour de l'hébergement")
      addToast(err.message || "Erreur lors de la mise à jour de l'hébergement", 'danger')
      throw err
    } finally {
      setLoading(false)
    }
  }

  const deleteAccommodation = async (id) => {
    // ✅ MULTI-TENANT: Vérifier module actif
    if (!isAccommodationsEnabled) {
      console.warn('🔇 AccommodationContext: Module accommodations désactivé')
      addToast('Le module hébergements est désactivé', 'warning')
      return false
    }

    try {
      setLoading(true)
      setError(null)

      console.log('Deleting accommodation:', id)

      await accommodationService.deleteAccommodation(id)

      console.log('Accommodation deleted')

      // Rafraîchir la liste des hébergements après suppression
      await fetchAccommodations(currentPage)

      addToast('Hébergement supprimé avec succès', 'success')
      return true
    } catch (err) {
      console.error("Erreur lors de la suppression de l'hébergement:", err)
      setError(err.message || "Erreur lors de la suppression de l'hébergement")
      addToast(err.message || "Erreur lors de la suppression de l'hébergement", 'danger')
      throw err
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = useCallback(
    (newFilters) => {
      // ✅ MULTI-TENANT: Vérifier module actif
      if (!isAccommodationsEnabled) {
        console.log('🔇 AccommodationContext: Module accommodations désactivé, skip filters')
        return
      }

      console.log('Applying filters:', newFilters)
      setFilters(newFilters)
      setCurrentPage(1) // Reset to first page when applying filters
      fetchAccommodations(1, 6, newFilters, searchTerm)
    },
    [searchTerm, fetchAccommodations, isAccommodationsEnabled],
  )

  const applySearch = useCallback(
    (term) => {
      // ✅ MULTI-TENANT: Vérifier module actif
      if (!isAccommodationsEnabled) {
        console.log('🔇 AccommodationContext: Module accommodations désactivé, skip search')
        return
      }

      console.log('Applying search:', term)
      setSearchTerm(term)
      setCurrentPage(1) // Reset to first page when searching
      fetchAccommodations(1, 6, filters, term)
    },
    [filters, fetchAccommodations, isAccommodationsEnabled],
  )

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const refreshAccommodations = useCallback(async () => {
    // ✅ MULTI-TENANT: Vérifier module actif
    if (!isAccommodationsEnabled) {
      console.log('🔇 AccommodationContext: Module accommodations désactivé, skip refresh')
      return
    }

    console.log('Refreshing accommodations')
    await fetchAccommodations(currentPage)
  }, [currentPage, fetchAccommodations, isAccommodationsEnabled])

  // NOUVEAU : Fonctions utilitaires
  const getAccommodationById = async (id) => {
    // ✅ MULTI-TENANT: Vérifier module actif
    if (!isAccommodationsEnabled) {
      console.warn('🔇 AccommodationContext: Module accommodations désactivé')
      return null
    }

    try {
      setError(null)
      console.log('Getting accommodation by ID:', id)

      const response = await accommodationService.getAccommodationById(id)

      console.log('Accommodation fetched by ID:', response)
      return response
    } catch (err) {
      console.error("Erreur lors de la récupération de l'hébergement:", err)
      setError(err.message || "Erreur lors de la récupération de l'hébergement")
      addToast(err.message || "Erreur lors de la récupération de l'hébergement", 'danger')
      throw err
    }
  }

  const validateAccommodation = async (id, status = 'active') => {
    // ✅ MULTI-TENANT: Vérifier module actif
    if (!isAccommodationsEnabled) {
      console.warn('🔇 AccommodationContext: Module accommodations désactivé')
      addToast('Le module hébergements est désactivé', 'warning')
      return null
    }

    try {
      setLoading(true)
      setError(null)

      console.log('Validating accommodation:', { id, status })

      const response = await accommodationService.validateAccommodation(id, status)

      console.log('Accommodation validated:', response)

      // Rafraîchir la liste
      await fetchAccommodations(currentPage)

      addToast('Hébergement validé avec succès', 'success')
      return response
    } catch (err) {
      console.error("Erreur lors de la validation de l'hébergement:", err)
      setError(err.message || "Erreur lors de la validation de l'hébergement")
      addToast(err.message || "Erreur lors de la validation de l'hébergement", 'danger')
      throw err
    } finally {
      setLoading(false)
    }
  }

  // ✅ MULTI-TENANT: N'effectuer l'appel initial que si le module est activé
  useEffect(() => {
    // Attendre que les modules soient chargés
    if (modulesLoading) {
      console.log('AccommodationContext: Attente du chargement des modules...')
      return
    }

    if (isAuthenticated && !isPublicPage && isAccommodationsEnabled) {
      console.log('Initial fetch accommodations - module actif')
      fetchAccommodations()
    } else if (!isAccommodationsEnabled) {
      console.log('🔇 Skipping initial fetch - module accommodations désactivé')
      setAccommodations([])
      setTotalPages(1)
    } else {
      console.log('Skipping initial fetch - not authenticated or on public page')
    }
  }, [fetchAccommodations, isAuthenticated, isPublicPage, isAccommodationsEnabled, modulesLoading])

  // Debug logging
  useEffect(() => {
    console.log('AccommodationContext state:', {
      accommodations: accommodations.length,
      totalPages,
      currentPage,
      loading,
      error,
      filters,
      searchTerm,
      isAuthenticated,
      isPublicPage,
      isAccommodationsEnabled,
      modulesLoading,
    })
  }, [
    accommodations,
    totalPages,
    currentPage,
    loading,
    error,
    filters,
    searchTerm,
    isAuthenticated,
    isPublicPage,
    isAccommodationsEnabled,
    modulesLoading,
  ])

  const contextValue = {
    // État
    accommodations,
    totalPages,
    currentPage,
    loading,
    error,
    filters,
    searchTerm,

    // Actions
    fetchAccommodations,
    createAccommodation,
    updateAccommodation,
    deleteAccommodation,
    getAccommodationById,
    validateAccommodation,
    applyFilters,
    applySearch,
    setCurrentPage,
    clearError,
    refreshAccommodations,

    // Utilitaires
    isAuthenticated,
    isPublicPage,
    // ✅ MULTI-TENANT: Exposer le statut du module
    isModuleEnabled: isAccommodationsEnabled,
    modulesLoading,
  }

  return (
    <AccommodationContext.Provider value={contextValue}>{children}</AccommodationContext.Provider>
  )
}
